import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../constants';
import useFetch from '../hooks/useFetch';
import { Grid, Typography, Dialog, DialogContent, DialogTitle, IconButton, DialogActions, TextField, Alert, Snackbar, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import { generateRandomPassword } from '../utils/passwordUtil';
import { getReadableErrorMessage } from '../utils/errorUtil';
import { LoadingButton } from '@mui/lab';

const GENERATE_PASSWORD_LENGTH = 8;

function NewUserDialog(props) {
    const { onDialogClose, afterUserCreate, open } = props;
    const { runFetch, data, error, loading } = useFetch({ url: `${BASE_URL}/user`, method: "POST" });
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [createWasJustClicked, setCreateWasJustClicked] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);

    const changeUserName = (userName) => {
        setCreateWasJustClicked(false);
        setUserName(userName);
    };

    const changePassword = (password) => {
        setCreateWasJustClicked(false);
        setPassword(password);
    };

    const createUser = () => {
        setCreateWasJustClicked(true);
        if (!userName || !password) {
            setOpenAlert(true);
            return;
        }
        runFetch({
            body: {
                userName: userName.trim(),
                password: password
            }
        });
    };

    useEffect(() => {
        if (!loading && !error && data) {
            closeDialog();
            afterUserCreate();
        } else if (!loading && error) {
            setErrorAlertOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error, loading]);

    const generatePassword = () => {
        const generatedPassword = generateRandomPassword(GENERATE_PASSWORD_LENGTH);
        setPassword(generatedPassword);
    };

    const closeDialog = () => {
        setUserName("");
        setPassword("");
        setCreateWasJustClicked(false);
        setOpenAlert(false);
        onDialogClose();
    };

    return (
        <>
            <Dialog
                maxWidth="sm"
                onClose={() => closeDialog()}
                aria-labelledby="new-user-dialog-title"
                open={open}
                transitionDuration={400}
            >
                <DialogTitle id="new-user-dialog-title">
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Grid item xs={10}>
                            <Typography variant="h5" component="h5">
                                Új felhasználó
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Grid container justifyContent="flex-end">
                                <IconButton aria-label="close" onClick={() => closeDialog()} size="large">
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <Grid container direction="column" spacing={2} sx={{ padding: '8px' }}>
                        <Grid item>
                            <TextField label="Felhasználónév" variant="outlined" value={userName} error={createWasJustClicked && !userName} onChange={(event) => changeUserName(event.target.value)} sx={{ width: '100%' }} />
                        </Grid>
                        <Grid item container direction="row" alignItems="center">
                            <Grid item xs={10}>
                                <TextField label="Jelszó" variant="outlined" value={password} error={createWasJustClicked && !password} onChange={(event) => changePassword(event.target.value)} sx={{ width: '100%' }} />
                            </Grid>
                            <Grid item xs={2} container justifyContent="center">
                                <Tooltip title="Jelszó generálása">
                                    <IconButton aria-label="generate-password" onClick={() => generatePassword()} size="large" variant="filled">
                                        <LockResetIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <Grid item container direction="row">
                            <Grid item>
                                <Alert severity="warning">A felhasználónevet és a jelszót küldd el a létrehozott felhasználónak, ezekkel fog tudni bejelentkezni. Első bejelentkezéskor a felhasználónak ezt a jelszót meg kell majd változtatnia.</Alert>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <LoadingButton sx={{ color: "#555" }} loading={loading} onClick={() => createUser()}>Létrehozás</LoadingButton>
                </DialogActions>
            </Dialog>
            <Snackbar open={openAlert && (!userName || !password)} onClose={() => setOpenAlert(false)} autoHideDuration={6000} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert severity="error" onClose={() => setOpenAlert(false)} sx={{ width: '100%' }}>
                    Az összes mező kitöltése kötelező!
                </Alert>
            </Snackbar>
            <Snackbar open={errorAlertOpen} autoHideDuration={6000} onClose={() => setErrorAlertOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setErrorAlertOpen(false)} severity="error" sx={{ width: '100%' }}>
                    {getReadableErrorMessage(error)}
                </Alert>
            </Snackbar>
        </>
    );
}

export default NewUserDialog;